import { useContext, useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { Download, FileText } from "lucide-react";
import { Button } from "@nexus/ui";
import {
  downloadAskNexusReport,
  type AskNexusReport,
  type AskNexusReportFailure,
  type AskNexusReportRequestCard,
} from "@/features/ask-nexus/api/ask-nexus.api";
import { AskNexusAlert } from "@/features/ask-nexus/components/AskNexusAlert";
import { AskNexusChatTenantContext } from "@/features/ask-nexus/components/AskNexusNexusLink";
import { AskNexusWorking } from "@/features/ask-nexus/components/AskNexusWorking";
import { useAskNexusAccess, useStartAskNexusReport } from "@/features/ask-nexus/hooks/useAskNexus";
import { ASK_NEXUS_ICON_STROKE } from "@/features/ask-nexus/lib/ui";
import { useAskNexusPanel } from "@/features/ask-nexus/store/ask-nexus-panel.store";
import { platformDisplayName } from "@/features/dashboard/lib/my-brand-filter-options";
import { askNexusReportQueryOptions } from "@/lib/queries/ask-nexus.queries";

export interface ReportTarget {
  tenantId: string;
  conversationId: string;
}

/** The card in a chat: its organization and conversation come from the open panel. */
export function AskNexusReportRequest({ card }: { card: AskNexusReportRequestCard }) {
  const tenantId = useContext(AskNexusChatTenantContext);
  const conversationId = useAskNexusPanel((s) => s.conversationId);
  if (!tenantId || !conversationId) return null;
  return <AskNexusReportCard card={card} target={{ tenantId, conversationId }} />;
}

/**
 * A report the person confirmed in this answer. The panel starts it once, for whoever may generate reports,
 * then shows its progress and, when ready, the download. The agent's closing lines are notes from the report
 * session, not something the person needs, so the card leaves them out. The server re-checks the request,
 * the caller and the tenant on every call.
 */
export function AskNexusReportCard({ card, target }: { card: AskNexusReportRequestCard; target: ReportTarget }) {
  const { t } = useTranslation();
  const access = useAskNexusAccess();
  const start = useStartAskNexusReport(target.tenantId, target.conversationId);
  const hasAsked = useRef(false);
  const reportId = card.report?.reportId ?? start.data?.reportId ?? "";
  const reportQuery = useQuery(askNexusReportQueryOptions(target.tenantId, target.conversationId, reportId));
  const report: AskNexusReport | null = reportQuery.data ?? start.data ?? card.report;
  const canStart = access.canGenerateReport && card.requestId !== null && card.report === null;

  useEffect(() => {
    if (!canStart || hasAsked.current || !card.requestId) return;
    hasAsked.current = true;
    start.mutate(card.requestId);
  }, [canStart, card.requestId, start]);

  const platform = platformDisplayName(t, card.platform);
  return (
    <section className="rounded-2xl border border-(--an-hair) bg-(--an-field) p-4" data-testid="ask-nexus-report-card">
      <div className="flex items-start gap-3">
        <FileText className="mt-0.5 size-4 flex-none text-(--an-ink-2)" strokeWidth={ASK_NEXUS_ICON_STROKE} aria-hidden="true" />
        <div className="min-w-0">
          <p className="text-[13px] font-medium">
            {t("askNexus.cards.report.title", "{{report}} report · {{platform}}", { report: card.reportType.label, platform })}
          </p>
          <p className="text-xs text-(--an-ink-2)">{card.reportType.description}</p>
        </div>
      </div>
      <div className="mt-3">
        <ReportState
          report={report}
          isStarting={start.isPending}
          startError={start.isError}
          canGenerate={access.canGenerateReport}
          target={target}
        />
      </div>
    </section>
  );
}

function ReportState({
  report,
  isStarting,
  startError,
  canGenerate,
  target,
}: {
  report: AskNexusReport | null;
  isStarting: boolean;
  startError: boolean;
  canGenerate: boolean;
  target: ReportTarget;
}) {
  const { t } = useTranslation();
  if (report?.status === "ready" && report.hasPowerPoint) return <ReadyReport report={report} target={target} />;
  if (report?.status === "cancelled") {
    return (
      <AskNexusAlert
        tone="neutral"
        title={t("askNexus.cards.report.cancelled", "This report was cancelled when Ask Nexus was closed. Ask again to make a new one.")}
      />
    );
  }
  if (report?.status === "failed") {
    return <AskNexusAlert tone="bad" title={t("askNexus.cards.report.failed", "The report could not be made")} description={failureText(t, report.failure)} />;
  }
  if (report?.status === "running" || isStarting) {
    return <AskNexusWorking text={t("askNexus.cards.report.preparing", "Preparing your PowerPoint. This takes a few minutes.")} />;
  }
  if (startError) {
    return <AskNexusAlert tone="bad" title={t("askNexus.cards.report.startFailed", "The report could not be started. Try again in a new chat.")} />;
  }
  if (!canGenerate) {
    return (
      <AskNexusAlert
        tone="neutral"
        title={t("askNexus.cards.report.noAccess", "Generating reports isn't turned on for your account in this organization.")}
      />
    );
  }
  return null;
}

function ReadyReport({ report, target }: { report: AskNexusReport; target: ReportTarget }) {
  const { t } = useTranslation();
  const [isDownloading, setIsDownloading] = useState(false);
  const [hasDownloadFailed, setHasDownloadFailed] = useState(false);

  const handleDownload = async () => {
    setIsDownloading(true);
    setHasDownloadFailed(false);
    try {
      const { blob, filename } = await downloadAskNexusReport(target.conversationId, report.reportId);
      saveBlob(blob, filename);
    } catch {
      setHasDownloadFailed(true);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <Button size="sm" className="self-start" onClick={() => void handleDownload()} disabled={isDownloading}>
        <Download className="size-3.5" strokeWidth={ASK_NEXUS_ICON_STROKE} aria-hidden="true" />
        {t("askNexus.cards.report.download", "Download PowerPoint")}
      </Button>
      {hasDownloadFailed ? (
        <p className="text-xs text-(--an-neg-ink)" role="alert">
          {t("askNexus.cards.report.downloadFailed", "The download did not work. Try again.")}
        </p>
      ) : null}
    </div>
  );
}

function failureText(t: TFunction, failure: AskNexusReportFailure | null): string {
  switch (failure) {
    case "timed_out":
      return t("askNexus.cards.report.failure.timedOut", "It took too long and was stopped.");
    case "limit_reached":
      return t("askNexus.cards.report.failure.limitReached", "It reached its usage limit before the file was ready.");
    case "no_file":
      return t("askNexus.cards.report.failure.noFile", "It finished without producing a PowerPoint.");
    default:
      return t("askNexus.cards.report.failure.other", "Something went wrong while making it. Ask again in a new chat.");
  }
}

/** Saves through a transient object URL, as the other Nexus exports do. */
function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}
